import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import styling from "./Home.module.css";
import coverImage from "../../assets/undraw_growth-analytics_bhy7.svg";
import { RiArrowRightSLine } from "react-icons/ri";

const featureSlides = [
  {
    title: "Clear stock overview",
    description:
      "See what is in stock, what is low, and what is out in a single clean view.",
  },
  {
    title: "Simple billing & receipts",
    description:
      "Create and print receipts in a few clicks. All bills stay saved automatically.",
  },
  {
    title: "Track spending & profit",
    description:
      "Add purchase costs and selling prices to understand your daily and monthly profit.",
  },
  {
    title: "Cloud backup, anywhere access",
    description:
      "Your data is stored online so you can access it from any device you sign in with.",
  },
];

function Home() {
  const loggedIn = useSelector((state) => state.isLogged);
  const [activeSlide, setActiveSlide] = useState(0);

  const handleNext = () => {
    setActiveSlide((prev) => (prev + 1) % featureSlides.length);
  };

  const handlePrev = () => {
    setActiveSlide((prev) =>
      prev === 0 ? featureSlides.length - 1 : prev - 1
    );
  };

  return (
    <>
      <Navbar />
      <main className={styling.page}>
        <div className={styling.container}>
          {/* HERO */}
          <section className={styling.heroSection}>
            <div className={styling.heroText}>
              <h1>Inventory management made simple.</h1>
              <p className={styling.heroSub}>
                A neat, focused dashboard to manage stock, sales, and expenses
                without extra noise.
              </p>
              <p className={styling.heroSubLight}>
                Built for small and medium shops that want an easy way to stay
                in control of daily numbers.
              </p>

              <div className={styling.buttonRow}>
                {loggedIn ? (
                  <Link to="/dashboard">
                    <div
                      className={`${styling.button} ${styling.buttonPrimary}`}
                    >
                      Go to Dashboard
                      <RiArrowRightSLine className={styling.buttonIcon} />
                    </div>
                  </Link>
                ) : (
                  <Link to="/signin">
                    <div
                      className={`${styling.button} ${styling.buttonPrimary}`}
                    >
                      Sign In
                      <RiArrowRightSLine className={styling.buttonIcon} />
                    </div>
                  </Link>
                )}

                <Link to="/demo">
                  <div
                    className={`${styling.button} ${styling.buttonSecondary}`}
                  >
                    View Demo
                    <RiArrowRightSLine className={styling.buttonIcon} />
                  </div>
                </Link>
              </div>

              <p className={styling.smallNote}>
                Full features like charts, history, and PDF receipts are
                available after you sign in.
              </p>
            </div>

            <div className={styling.heroImageWrap}>
              <img
                src={coverImage}
                alt="Inventory dashboard preview"
                className={styling.heroImage}
              />
            </div>
          </section>

          {/* SECTION 2: WHAT YOU CAN DO */}
          <section className={styling.section} id="features">
            <header className={styling.sectionHeader}>
              <h2>Everything in one place.</h2>
              <p>
                You don’t need separate notebooks, Excel sheets, and phone
                calculators. Manage it all from one screen.
              </p>
            </header>

            <div className={styling.featureGrid}>
              <div className={styling.card}>
                <h3 className={styling.subHeading}>Add & update items</h3>
                <p>
                  Store item name, quantity, cost price, and selling price.
                  Update quantities as you buy and sell.
                </p>
              </div>
              <div className={styling.card}>
                <h3 className={styling.subHeading}>Sell & print receipts</h3>
                <p>
                  Create bills, apply quantities, and generate clean PDF
                  receipts that can be printed or shared.
                </p>
              </div>
              <div className={styling.card}>
                <h3 className={styling.subHeading}>See simple charts</h3>
                <p>
                  View basic charts to understand which items move fast and how
                  your daily totals are changing.
                </p>
              </div>
              <div className={styling.card}>
                <h3 className={styling.subHeading}>Keep data safe</h3>
                <p>
                  Your shop data lives in the cloud and stays linked to your
                  account so it’s not lost with a broken phone.
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 3: SLIDE-STYLE HIGHLIGHTS */}
          <section className={styling.section}>
            <header className={styling.sectionHeader}>
              <h2>How it helps in daily work.</h2>
              <p>
                A few examples of how the dashboard fits into your regular shop
                routine.
              </p>
            </header>

            <div className={styling.sliderRow}>
              <button
                type="button"
                className={styling.sliderArrow}
                onClick={handlePrev}
                aria-label="Previous highlight"
              >
                ‹
              </button>

              <div className={styling.sliderCard}>
                <h3 className={styling.subHeading}>
                  {featureSlides[activeSlide].title}
                </h3>
                <p>{featureSlides[activeSlide].description}</p>
              </div>

              <button
                type="button"
                className={styling.sliderArrow}
                onClick={handleNext}
                aria-label="Next highlight"
              >
                ›
              </button>
            </div>

            <div className={styling.sliderDots}>
              {featureSlides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveSlide(index)}
                  className={`${styling.dot} ${
                    index === activeSlide ? styling.dotActive : ""
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </section>

          {/* SECTION 4: SIMPLE STEPS */}
          <section className={styling.section}>
            <header className={styling.sectionHeader}>
              <h2>Get started in three simple steps.</h2>
            </header>

            <div className={styling.stepsRow}>
              <div className={styling.stepCard}>
                <span className={styling.stepNumber}>1</span>
                <h3 className={styling.subHeading}>
                  Create or sign in to your account
                </h3>
                <p>Use your email to sign in and secure your shop’s data.</p>
              </div>
              <div className={styling.stepCard}>
                <span className={styling.stepNumber}>2</span>
                <h3 className={styling.subHeading}>Add your main products</h3>
                <p>
                  Start with your common items, quantities, and prices. You can
                  refine later.
                </p>
              </div>
              <div className={styling.stepCard}>
                <span className={styling.stepNumber}>3</span>
                <h3 className={styling.subHeading}>Use it daily for sales</h3>
                <p>
                  Bill customers, update stock, and check totals at day end from
                  one dashboard.
                </p>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className={styling.section}>
            <div className={styling.finalCta}>
              <div>
                <h2>Try the demo or jump straight in.</h2>
                <p>
                  Open the live demo to see how it looks, or sign in and start
                  using it with your real shop data.
                </p>
              </div>
              <div className={styling.finalButtons}>
                <Link to="/demo">
                  <div className={`${styling.button} ${styling.buttonPrimary}`}>
                    Open Demo
                    <RiArrowRightSLine className={styling.buttonIcon} />
                  </div>
                </Link>

                {loggedIn ? (
                  <Link to="/dashboard">
                    <div
                      className={`${styling.button} ${styling.buttonTertiary}`}
                    >
                      Go to My Dashboard
                    </div>
                  </Link>
                ) : (
                  <Link to="/signin">
                    <div
                      className={`${styling.button} ${styling.buttonTertiary}`}
                    >
                      Sign In
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </section>

          {/* FOOTER */}
          <footer className={styling.footer}>
            <div className={styling.footerTop}>
              <div>
                <h4>Inventory Platform</h4>
                <p>Simple inventory and billing for everyday shops.</p>
              </div>
              <div className={styling.footerLinks}>
                <a href="#features">Features</a>
                <Link to="/demo">Demo</Link>
                {/* <a href="/privacy">Privacy</a> */}
              </div>
            </div>
            <p className={styling.footerBottom}>
              © {new Date().getFullYear()} Inventory Platform. All rights
              reserved.
            </p>
          </footer>
        </div>
      </main>
    </>
  );
}

export default Home;
